import React from 'react';
import {Button} from 'react-bootstrap';
import {DefaultModal} from '../../components';
import {ModalsStore, PriceAlertsStore} from '../../stores';
import {ModalsAction, PriceAlertsAction} from '../../actions';
import {convertModalPayload, getAlertLocation, getAlertLocationLabel, formatPercentageChange, formatPriceWithUnits} from './helpers';

export default class SkipAlertModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      alertData: props.isBulkMode ? [] : {},  // Array for bulk, object for single
      isSubmitting: false
    };

    this.handleModalChange = this.handleModalChange.bind(this);
    this.handleSkip = this.handleSkip.bind(this);
    this.handleStoreChange = this.handleStoreChange.bind(this);
  }

  componentDidMount() {
    ModalsStore.addChangeListener(this.handleModalChange);
    PriceAlertsStore.addChangeListener(this.handleStoreChange);
    // Load initial modal data if modal is already visible
    this.handleModalChange();
  }

  componentWillUnmount() {
    ModalsStore.removeChangeListener(this.handleModalChange);
    PriceAlertsStore.removeChangeListener(this.handleStoreChange);
  }

  handleStoreChange() {
    // If modal is still submitting and store updated successfully, close modal
    if (this.state.isSubmitting) {
      this.setState({isSubmitting: false});
      ModalsAction.hide(this.props.id);
    }
  }

  handleModalChange() {
    if (ModalsStore.isModalVisible(this.props.id)) {
      let payload = ModalsStore.getPayload(this.props.id);
      
      if (this.props.isBulkMode) {
        // Bulk mode: payload contains {alerts: [...], isBulk: true}
        // Convert Immutable Map to plain JS object if needed
        if (payload && payload.toJS) {
          payload = payload.toJS();
        }
        
        this.setState({
          alertData: payload && payload.alerts ? payload.alerts : []
        });
      } else {
        // Single mode: payload is the alert data
        const alertData = convertModalPayload(payload);
        this.setState({
          alertData: alertData || {}
        });
      }
    }
  }

  handleSkip() {
    const {alertData} = this.state;

    this.setState({isSubmitting: true});

    if (this.props.isBulkMode) {
      // Bulk skip multiple alerts
      const alertIds = alertData.map(alert => alert.id);
      PriceAlertsAction.bulkSkipAlerts(alertIds);
    } else {
      // Single alert skip
      PriceAlertsAction.skipAlert(alertData.id);
    }
    // Modal will close when store updates via handleStoreChange
  }

  render() {
    const {alertData, isSubmitting} = this.state;
    const {isBulkMode} = this.props;

    // Loading state
    if (!alertData || (isBulkMode ? alertData.length === 0 : Object.keys(alertData).length === 0)) {
      return (
        <DefaultModal
          id={this.props.id}
          title={this.props.title}
          onHide={this.props.onHide}
          bsSize="medium">
          <div>Loading...</div>
        </DefaultModal>
      );
    }

    if (isBulkMode) {
      // Bulk mode: display list of alerts
      return (
        <DefaultModal
          id={this.props.id}
          title={this.props.title}
          onHide={this.props.onHide}
          bsSize="large">
          <div>
            <p>
              Are you sure you want to mark <strong>{alertData.length} alert{alertData.length > 1 ? 's' : ''}</strong> as <strong>skipped</strong>?
            </p>
            <div style={{maxHeight: '300px', overflowY: 'auto', marginBottom: '15px', border: '1px solid #ddd', padding: '10px', borderRadius: '4px'}}>
              {alertData.map((alert, index) => {
                const percentage = formatPercentageChange(alert.percentageChange);
                const previousPrice = formatPriceWithUnits(alert.previousValue, alert);
                const newPrice = formatPriceWithUnits(alert.value, alert);
                return (
                  <div key={alert.id} style={{
                    padding: '8px',
                    marginBottom: index < alertData.length - 1 ? '8px' : '0',
                    borderBottom: index < alertData.length - 1 ? '1px solid #eee' : 'none'
                  }}>
                    <div><strong>{getAlertLocationLabel(alert)} </strong>{getAlertLocation(alert)}</div>
                    <div style={{fontSize: '0.9em', color: '#666'}}>
                      <strong>Supplier: </strong>{alert.Supplier ? alert.Supplier.name : 'N/A'}
                    </div>
                    <div style={{fontSize: '0.9em', color: '#666'}}>
                      <strong>Previous Price: </strong>{previousPrice} | {' '}
                      <strong>New Price: </strong>{newPrice}
                    </div>
                    <div style={{fontSize: '0.9em', color: '#666'}}>
                      <strong>Change: </strong>
                      <span className={percentage.className}>{percentage.text}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{marginTop: '10px'}}>
              <small><em>This action will mark all selected price changes as reviewed and acceptable.</em></small>
            </div>

            <div style={{marginTop: '20px'}}>
              <Button
                bsStyle="primary"
                onClick={this.handleSkip}
                disabled={isSubmitting}>
                {isSubmitting ? 'Skipping...' : `Skip ${alertData.length} Alert${alertData.length > 1 ? 's' : ''}`}
              </Button>
              <span> </span>
              <Button onClick={this.props.onHide}>Cancel</Button>
            </div>
          </div>
        </DefaultModal>
      );
    }

    // Single mode: display single alert
    const percentage = formatPercentageChange(alertData.percentageChange);

    return (
      <DefaultModal
        id={this.props.id}
        title={this.props.title}
        onHide={this.props.onHide}
        bsSize="medium">
        <div>
          <p>
            Are you sure you want to mark this alert as <strong>skipped</strong>?
          </p>
          <p>
            <strong>{getAlertLocationLabel(alertData)} </strong>{getAlertLocation(alertData)}<br/>
            <strong>Supplier: </strong>{alertData.Supplier ? alertData.Supplier.name : 'N/A'}<br/>
            <strong>Price Change: </strong>
            <span className={percentage.className}>{percentage.text}</span>
          </p>
          <div style={{marginTop: '10px'}}>
            <small><em>This action will mark the price change as reviewed and acceptable.</em></small>
          </div>

          <div style={{marginTop: '20px'}}>
            <Button
              bsStyle="primary"
              onClick={this.handleSkip}
              disabled={isSubmitting}>
              {isSubmitting ? 'Skipping...' : 'Skip Alert'}
            </Button>
            <span> </span>
            <Button onClick={this.props.onHide}>Cancel</Button>
          </div>
        </div>
      </DefaultModal>
    );
  }
}
